import React, { useState } from "react";
import "./FeedbackWidget.css";
import { ApiClient } from "../generated/api";
import CONFIG_KEYS from "../config";
import { customFetch } from "../middlewares/custom-fetch";

type Rating = 1 | 2 | 3 | 4 | 5;

const EMOJI_MAP: Record<Rating, { icon: string; label: string }> = {
    1: { icon: "😞", label: "Very Bad"  },
    2: { icon: "😕", label: "Bad"       },
    3: { icon: "😐", label: "Okay"      },
    4: { icon: "😊", label: "Good"      },
    5: { icon: "🤩", label: "Excellent" },
};

const FeedbackWidget: React.FC = () => {
    const [open,      setOpen]      = useState(false);
    const [rating,    setRating]    = useState<Rating | null>(null);
    const [hovered,   setHovered]   = useState<Rating | null>(null);
    const [message,   setMessage]   = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!rating) return;
        setLoading(true);
        setError(null);
        try {
            const api = new ApiClient(CONFIG_KEYS.baseUrl, { fetch: customFetch });
            await api.submit({ rating, message: message || undefined });
            setSubmitted(true);
            setTimeout(() => {
                setOpen(false);
                setSubmitted(false);
                setRating(null);
                setMessage("");
            }, 2200);
        } catch {
            setError("Could not send feedback. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const activeRating = hovered ?? rating;

    return (
        <>
            {/* ── Slide-in panel ── */}
            <div className={`fbw-panel ${open ? "fbw-panel--open" : ""}`} role="dialog" aria-label="Feedback">
                <div className="fbw-header">
                    <span className="fbw-header-title">Share your feedback</span>
                    <button className="fbw-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>
                </div>

                {submitted ? (
                    <div className="fbw-thank-you">
                        <span className="fbw-thank-icon">🎉</span>
                        <p>Thank you for your feedback!</p>
                    </div>
                ) : (
                    <form className="fbw-body" onSubmit={handleSubmit}>
                        <p className="fbw-question">How would you rate your experience?</p>

                        {/* Emoji rating */}
                        <div className="fbw-emojis">
                            {(Object.keys(EMOJI_MAP) as unknown as Rating[]).map(r => {
                                const n = Number(r) as Rating;
                                const active = activeRating === n;
                                return (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`fbw-emoji-btn ${active ? "fbw-emoji-btn--active" : ""}`}
                                        onClick={() => setRating(n)}
                                        onMouseEnter={() => setHovered(n)}
                                        onMouseLeave={() => setHovered(null)}
                                        aria-label={EMOJI_MAP[n].label}
                                        title={EMOJI_MAP[n].label}
                                    >
                                        <span className="fbw-emoji">{EMOJI_MAP[n].icon}</span>
                                        <span className="fbw-emoji-label">{EMOJI_MAP[n].label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Message */}
                        <label className="fbw-label" htmlFor="fbw-msg">
                            Tell us more <span className="fbw-optional">(optional)</span>
                        </label>
                        <textarea
                            id="fbw-msg"
                            className="fbw-textarea"
                            rows={4}
                            placeholder="Your thoughts…"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            maxLength={500}
                        />
                        <span className="fbw-char-count">{message.length}/500</span>

                        {error && <p className="fbw-error">{error}</p>}

                        <button
                            type="submit"
                            className="fbw-submit"
                            disabled={!rating || loading}
                        >
                            {loading ? "Sending…" : "Send Feedback"}
                        </button>
                    </form>
                )}
            </div>

            {/* ── Vertical tab trigger ── */}
            <button
                className={`fbw-tab ${open ? "fbw-tab--open" : ""}`}
                onClick={() => setOpen(p => !p)}
                aria-label="Open feedback"
                title="Feedback"
            >
                <span className="fbw-tab-text">Feedback 💬</span>
            </button>

            {/* ── Backdrop (mobile) ── */}
            {open && <div className="fbw-backdrop" onClick={() => setOpen(false)} />}
        </>
    );
};

export default FeedbackWidget;
